import useLocalStorage from '../hooks/useLocalStorage';
import useQueryParams from '../hooks/useQueryParams';
import { toggleDocumentAttribute } from '../utils/layout';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
const ThemeContext = createContext(undefined);
const useLayoutContext = () => {
  const context = use(ThemeContext);
  if (!context) {
    throw new Error('useLayoutContext can only be used within LayoutProvider');
  }
  return context;
};

// const getPreferredTheme = (): ThemeType => (window.matchMedia('(przefers-color-scheme: dark)').matches ? 'dark' : 'light')

const LayoutProvider = ({
  children
}) => {
  const queryParams = useQueryParams();
  const override = !!(queryParams.layout_theme || queryParams.topbar_theme || queryParams.menu_theme || queryParams.menu_size || queryParams.layout_mode);
  const INIT_STATE = {
    theme: queryParams['layout_theme'] ? queryParams['layout_theme'] : 'light',
    layoutOrientation: queryParams['layout_type'] ? queryParams['layout_type'] : 'vertical',
    topbarTheme: queryParams['topbar_theme'] ? queryParams['topbar_theme'] : 'brand',
    menu: {
      theme: queryParams['menu_theme'] ? queryParams['menu_theme'] : 'light',
      size: queryParams['menu_size'] ? queryParams['menu_size'] : 'default'
    },
    layoutMode: queryParams['layout_mode'] ? queryParams['layout_mode'] : 'fluid'
  };
  const [settings, setSettings] = useLocalStorage('__ABSTACK_NEXT_CONFIG__', INIT_STATE, override);
  const [offcanvasStates, setOffcanvasStates] = useState({
    showThemeCustomizer: false,
    showHorizontalMenu: false,
    // showActivityStream: false,
    showBackdrop: false
  });

  // update settings
  const updateSettings = _newSettings => setSettings({
    ...settings,
    ..._newSettings
  });

  // update theme mode
  const changeTheme = newTheme => {
    updateSettings({
      theme: newTheme
    });
  };

  // change topbar theme
  const changeTopbarTheme = newTheme => {
    updateSettings({
      topbarTheme: newTheme
    });
  };
  const changeLayoutMode = newMode => {
    updateSettings({
      layoutMode: newMode
    });
  };
  const changeLayoutOrientation = newOrientation => {
    updateSettings({
      layoutOrientation: newOrientation
    });
  };

  // change menu theme
  const changeMenuTheme = newTheme => {
    updateSettings({
      menu: {
        ...settings.menu,
        theme: newTheme
      }
    });
  };

  // change menu theme
  const changeMenuSize = newSize => {
    updateSettings({
      menu: {
        ...settings.menu,
        size: newSize
      }
    });
  };

  // toggle theme customizer offcanvas
  const toggleThemeCustomizer = () => {
    setOffcanvasStates(s => ({
      ...s,
      showThemeCustomizer: !s.showThemeCustomizer
    }));
  };
  const toggleHorizontalMenu = () => {
    setOffcanvasStates(s => ({
      ...s,
      showHorizontalMenu: !s.showHorizontalMenu
    }));
  };
  const themeCustomizer = {
    open: offcanvasStates.showThemeCustomizer,
    toggle: toggleThemeCustomizer
  };
  const horizontalMenu = {
    open: offcanvasStates.showHorizontalMenu,
    toggle: toggleHorizontalMenu
  };

  // The phone-size sidebar (menu size "full") opens over a backdrop. The <html> class
  // (which also locks the page scroll) follows the state, so the two can't drift apart.
  const toggleBackdrop = useCallback(() => {
    setOffcanvasStates(s => ({
      ...s,
      showBackdrop: !s.showBackdrop
    }));
  }, []);
  const closeBackdrop = useCallback(() => {
    setOffcanvasStates(s => (s.showBackdrop ? {
      ...s,
      showBackdrop: false
    } : s));
  }, []);
  useEffect(() => {
    document.documentElement.classList.toggle('sidebar-enable', offcanvasStates.showBackdrop);
  }, [offcanvasStates.showBackdrop]);
  // Close it when the window grows past phone size (it used to stay over the desktop page),
  // on every navigation, and on Escape.
  const location = useLocation();
  useEffect(() => {
    if (settings.menu.size !== 'full') closeBackdrop();
  }, [settings.menu.size, closeBackdrop]);
  useEffect(() => {
    closeBackdrop();
  }, [location.pathname, closeBackdrop]);
  useEffect(() => {
    if (!offcanvasStates.showBackdrop) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') closeBackdrop();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [offcanvasStates.showBackdrop, closeBackdrop]);
  useEffect(() => {
    toggleDocumentAttribute('data-bs-theme', settings.theme);
    toggleDocumentAttribute('data-topbar-color', settings.topbarTheme);
    toggleDocumentAttribute('data-menu-color', settings.menu.theme);
    toggleDocumentAttribute('data-sidenav-size', settings.menu.size);
    toggleDocumentAttribute('data-layout-mode', settings.layoutMode);
    return () => {
      toggleDocumentAttribute('data-bs-theme', settings.theme, true);
      toggleDocumentAttribute('data-topbar-color', settings.topbarTheme, true);
      toggleDocumentAttribute('data-sidenav-size', settings.menu.theme, true);
      toggleDocumentAttribute('data-layout-mode', settings.layoutMode, true);
    };
  }, [settings]);
  const resetSettings = () => updateSettings(INIT_STATE);
  return <ThemeContext.Provider value={useMemo(() => ({
    ...settings,
    horizontalMenu,
    themeMode: settings.theme,
    changeTheme,
    changeLayoutMode,
    changeTopbarTheme,
    changeLayoutOrientation,
    changeMenu: {
      theme: changeMenuTheme,
      size: changeMenuSize
    },
    themeCustomizer,
    toggleBackdrop,
    resetSettings
  }), [settings, offcanvasStates])}>
    {children}
    {offcanvasStates.showBackdrop && <div className="offcanvas-backdrop fade show" onClick={toggleBackdrop} />}
  </ThemeContext.Provider>;
};
export { LayoutProvider, useLayoutContext };